import { api, ApiError } from '../api/client';
import { uploadBlob } from '../api/blobUpload';
import {
  decryptAesGcm,
  encryptAesGcm,
  importAesKey,
  packEncryptedBlob,
  unpackEncryptedBlob,
} from './primitives';
import type { SharedFolderActorRef } from './sharedFolderManifest';

export const FOLDER_ACTIVITY_AAD_PREFIX = 'shareweb:folder-activity:v1|';

export type FolderActivityAction =
  | 'upload'
  | 'create_directory'
  | 'rename'
  | 'move'
  | 'delete';

export interface FolderActivityEntry {
  ts: string;
  actor: SharedFolderActorRef;
  action: FolderActivityAction;
  path?: string;
  fromPath?: string;
  toPath?: string;
  entryId?: string;
}

export interface FolderActivityPlaintext {
  entries: FolderActivityEntry[];
}

function activityAad(folderId: string): Uint8Array {
  return new TextEncoder().encode(`${FOLDER_ACTIVITY_AAD_PREFIX}${folderId}`);
}

export async function encryptFolderActivity(
  folderKey: Uint8Array,
  folderId: string,
  plaintext: FolderActivityPlaintext,
): Promise<Uint8Array> {
  const key = await importAesKey(folderKey);
  const encrypted = await encryptAesGcm(
    key,
    new TextEncoder().encode(JSON.stringify(plaintext)),
    activityAad(folderId),
  );
  return packEncryptedBlob(encrypted);
}

export async function decryptFolderActivity(
  folderKey: Uint8Array,
  folderId: string,
  blob: Uint8Array,
): Promise<FolderActivityPlaintext> {
  const key = await importAesKey(folderKey);
  const decrypted = await decryptAesGcm(key, unpackEncryptedBlob(blob), activityAad(folderId));
  const parsed = JSON.parse(new TextDecoder().decode(decrypted)) as FolderActivityPlaintext;
  return {
    entries: Array.isArray(parsed.entries) ? parsed.entries : [],
  };
}

export interface AppendFolderActivityOptions {
  folderId: string;
  folderKey: Uint8Array;
  entry: Omit<FolderActivityEntry, 'ts'> & { ts?: string };
  guest?: { folderToken: string; guestInviteId: string };
}

async function loadActivity(
  folderId: string,
  folderKey: Uint8Array,
  activityBlobId: string | null,
): Promise<FolderActivityPlaintext> {
  if (!activityBlobId) {
    return { entries: [] };
  }

  const bytes = await api.fetchBlob(activityBlobId);
  return decryptFolderActivity(folderKey, folderId, bytes);
}

export async function loadFolderActivityLog(
  folderId: string,
  folderKey: Uint8Array,
): Promise<FolderActivityEntry[]> {
  const meta = await api.getSharedFolderActivity(folderId);
  const plaintext = await loadActivity(folderId, folderKey, meta.activityBlobId);
  return [...plaintext.entries].sort((a, b) => b.ts.localeCompare(a.ts));
}

export async function appendFolderActivity(options: AppendFolderActivityOptions): Promise<number> {
  const entry: FolderActivityEntry = {
    ...options.entry,
    ts: options.entry.ts ?? new Date().toISOString(),
  };

  const maxAttempts = 3;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const meta = options.guest
      ? await api.getSharedFolderGuestActivity(options.guest.folderToken, options.guest.guestInviteId)
      : await api.getSharedFolderActivity(options.folderId);

    const current = await loadActivity(options.folderId, options.folderKey, meta.activityBlobId);
    const next: FolderActivityPlaintext = { entries: [...current.entries, entry] };
    const encrypted = await encryptFolderActivity(options.folderKey, options.folderId, next);
    const activityBlobId = await uploadBlob(encrypted);

    try {
      if (options.guest) {
        const committed = await api.commitSharedFolderGuestActivity(
          options.guest.folderToken,
          options.guest.guestInviteId,
          { expectedRevision: meta.activityRevision, activityBlobId },
        );
        return committed.revision;
      }

      const committed = await api.commitSharedFolderActivity(options.folderId, {
        expectedRevision: meta.activityRevision,
        activityBlobId,
      });
      return committed.revision;
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 409 || attempt === maxAttempts - 1) {
        throw error;
      }
    }
  }

  throw new Error('activity_commit_failed');
}

/** Best-effort append; never throws into the mutation UX. */
export function recordFolderActivitySafe(options: AppendFolderActivityOptions): void {
  void appendFolderActivity(options).catch((cause) => {
    console.error('Folder activity append failed', cause);
  });
}
